// Legal content for forecastly.me — drafted for ProfitMIND Ltd (the legal entity).
// These are temporary in-app pages; they're designed to be lifted into a static
// site at profitmind.co.uk later (just paste the constants into your CMS).
//
// IMPORTANT: This is a working draft based on Magdalena's existing profitmind.co.uk
// privacy policy + DPA-2018 templates. Magdalena should review the wording and
// edit before deploying to production for the £97 / £15/mo paid plans.

export const LEGAL_LAST_UPDATED = "12 May 2026";

export const COMPANY = {
  legal_name: "ProfitMIND Ltd",
  trading_name: "forecastly.me",
  address: "4 Cranes Farm Road, Basildon, Essex SS14 3JJ, United Kingdom",
  email: "hello@profitmind.co.uk",
  ico_url: "https://ico.org.uk/",
};

// ============================================================
// PRIVACY POLICY — covers ProfitMIND Ltd including forecastly.me
// ============================================================
export const PRIVACY_POLICY = `
1. INTRODUCTION

This privacy notice tells you how we collect and process your personal data when you use forecastly.me (the cashflow forecasting software) or our website at profitmind.co.uk. forecastly.me is a trading name of ProfitMIND Ltd. By using the software, you confirm you are over 18 years of age and entering into the agreement on behalf of yourself or your business.

"ProfitMIND Ltd" is the data controller and is responsible for your personal data (referred to as "we", "us" or "our" in this notice).

CONTACT DETAILS

Legal entity: ProfitMIND Ltd
Trading as: forecastly.me
Postal address: 4 Cranes Farm Road, Basildon, Essex SS14 3JJ, United Kingdom
Email: hello@profitmind.co.uk

It is important that the information we hold about you is accurate and current. Please update us by email or in your account settings if anything changes.

2. WHAT DATA WE COLLECT, WHY, AND ON WHAT LAWFUL BASIS

Personal data means any information that identifies an individual. We may process the following categories about you:

ACCOUNT DATA — your name, email address, password (stored only as a one-way bcrypt hash — we never see it), business name, and chosen pricing plan. Purpose: to create and run your account. Lawful basis: performance of a contract.

FINANCIAL DATA YOU ENTER — your business income, costs, opening cash, personal pay, monthly actuals, goals, and any notes you choose to add. This is YOUR business data; we treat it as confidential. Purpose: to compute your forecast, variance and AI insights, and to display them back to you. Lawful basis: performance of a contract.

PAYMENT DATA — handled by Stripe (our payment processor). We receive only the limited transaction metadata Stripe shares with us (customer id, plan id, payment status, last 4 digits of card if applicable). We do NOT store your card number. Purpose: to take payment and reconcile your subscription. Lawful basis: performance of a contract.

COMMUNICATION DATA — any emails or replies you send us, the contents of contact-form submissions, and our responses. Purpose: customer support, record-keeping, and to establish, pursue or defend legal claims. Lawful basis: our legitimate interest in operating the business.

TECHNICAL DATA — your IP address, browser type, the pages you visit on our site, time-zone settings and similar device data. We do not currently run analytics on the forecastly.me application itself; basic technical logs are kept by our hosting provider for security and abuse-prevention. Purpose: to keep the service running and secure. Lawful basis: our legitimate interest.

MARKETING DATA — if you have opted in via the website or by becoming a customer, we may keep your email address in our MailerLite list to send you product updates and occasional helpful content. You can unsubscribe with one click in any email. Lawful basis: consent (for marketing-only opt-ins) or our legitimate interest (for product updates to existing customers).

SENSITIVE DATA — we do NOT collect or process any sensitive personal data (race, ethnicity, religion, sexual orientation, political opinions, health, biometric data or criminal records).

AUTOMATED DECISION MAKING — we use AI (Anthropic's Claude model via Emergent Integrations) to generate narrative insights from your forecast and actuals. These insights are suggestions; they do not produce any legal or financial decision that meaningfully affects you. You always remain in control.

3. HOW WE COLLECT YOUR DATA

DIRECTLY FROM YOU — when you sign up, fill out your onboarding wizard, enter actuals, set goals, or contact us by email or the in-app contact form.

AUTOMATICALLY — basic technical data (IP, browser) when you load the site, and authentication cookies set when you log in (see our Cookie Policy).

FROM THIRD PARTIES we use to run the service:
  • Stripe (payments) — transaction metadata when you pay or subscribe.
  • MailerLite (marketing emails) — subscription status and engagement data if you receive our emails.
  • Resend (transactional emails) — delivery status for emails we send you.

4. MARKETING COMMUNICATIONS

We will only send you marketing emails if you have explicitly opted in OR you are an existing customer and we have a legitimate interest in keeping you informed about product changes (under the Privacy and Electronic Communications Regulations). You can unsubscribe at any time using the link in every email, or by emailing hello@profitmind.co.uk.

Opting out of marketing does NOT affect transactional emails (password resets, payment receipts, account-deletion confirmations, monthly nudges to log your actuals).

We do not share your personal data with third parties for their own marketing purposes without your express consent.

5. WHO WE SHARE YOUR DATA WITH

We share your data with the following categories of service provider, strictly for the purpose of running the service:

  • Stripe (Stripe Payments Europe Ltd) — payment processing.
  • MailerLite (UAB MailerLite, Lithuania / MailerLite Inc., US) — marketing email delivery.
  • Resend (Resend.com Inc., US) — transactional email delivery.
  • Anthropic (via Emergent Integrations) — AI insight generation. Only the relevant numbers (forecast headlines, this month's actuals, and your goals) are sent at the moment of generation. No personal account identifiers are shared.
  • MongoDB Atlas — encrypted database hosting (EU region).
  • Emergent — application hosting infrastructure.
  • Our professional advisers (accountants, lawyers, insurers) when strictly necessary.
  • Government bodies where we are required by law to disclose data.

Each of these providers is bound by contract and by data protection law to keep your data secure and use it only for the purposes we instruct.

We may also share your data if we sell, transfer or merge parts of our business or our assets. We will require any acquirer to honour this privacy notice.

6. INTERNATIONAL TRANSFERS

Some of our service providers are located outside the UK (notably Anthropic, MailerLite Inc. and Resend in the US). We rely on the UK government's adequacy regulations, UK-approved Standard Contractual Clauses and the UK-US Data Bridge to ensure your data has equivalent legal protection wherever it is processed.

7. DATA SECURITY

We have implemented technical and organisational measures to protect your data — including bcrypt password hashing, HTTPS everywhere, encrypted database connections, access controls limited to authorised personnel, and a documented incident response procedure. We will notify you and the ICO of any personal data breach where we are required by law.

8. DATA RETENTION

We keep your data only for as long as we need it:
  • Account, forecast, actuals and goals data: while your account is active. When you delete your account from inside the app, we permanently erase this data within 24 hours.
  • Payment records: 6 years from the last transaction, to comply with HMRC tax record-keeping rules.
  • Marketing list: until you unsubscribe.
  • Support emails: 3 years from the last response.

When we no longer need your personal data we either delete it or anonymise it for research/statistics.

9. YOUR LEGAL RIGHTS

Under UK GDPR you have the right to:
  • REQUEST ACCESS to your personal data ("subject access request").
  • REQUEST CORRECTION of any data we hold that is wrong or incomplete.
  • REQUEST ERASURE of your data (the "right to be forgotten").
  • OBJECT to processing where we rely on legitimate interests.
  • REQUEST RESTRICTION of processing in specific circumstances.
  • REQUEST PORTABILITY — receive your data in a structured, commonly used, machine-readable format.
  • WITHDRAW CONSENT at any time where consent is the lawful basis.

You can exercise the right to portability and the right to erasure DIRECTLY from inside your account settings — go to your profile → "Export my data" or "Delete my account". For any other request, please email hello@profitmind.co.uk.

We respond to all legitimate requests within one month. There is no fee unless the request is clearly unfounded, repetitive or excessive.

If you are not satisfied with how we handle your data, you have the right to complain to the Information Commissioner's Office (ICO), the UK's data protection regulator — www.ico.org.uk. We would appreciate the chance to address the issue with you first.

10. THIRD-PARTY LINKS

Our website and emails may include links to third-party websites and tools. We are not responsible for their privacy practices. When you leave our site, please read the privacy notice of every site you visit.

11. CHILDREN

forecastly.me is intended for use by businesses and self-employed adults. We do not knowingly collect data about anyone under the age of 18.

12. CHANGES TO THIS NOTICE

We may update this privacy notice from time to time. The "Last updated" date at the top of the page reflects the most recent change. Significant changes will be notified to active customers by email.
`.trim();

// ============================================================
// COOKIE POLICY
// ============================================================
export const COOKIE_POLICY = `
1. WHAT COOKIES ARE

Cookies are small text files stored on your device when you visit a website. They help the site remember things like whether you're logged in.

2. COOKIES WE USE

We currently only use STRICTLY NECESSARY cookies and equivalent browser storage. We do NOT use analytics, marketing, advertising or tracking cookies on the forecastly.me application.

ESSENTIAL / FUNCTIONAL
  • Session token (JWT) — required to keep you logged in. Without it you would need to log in on every page. Lasts 30 days or until you log out.
  • Onboarding draft (localStorage) — saves your in-progress onboarding wizard so you can come back later. Stored only in your browser; we cannot read it.
  • What-If scenarios (localStorage) — saves up to 3 stress-test scenarios from the dashboard slider. Stored only in your browser.
  • Cookie-consent confirmation (localStorage) — remembers that you have acknowledged this notice so the banner doesn't show on every page.

THIRD-PARTY COOKIES
When you go through the Stripe Checkout page to pay or upgrade, Stripe sets its own cookies to detect fraud and remember session state. These are governed by Stripe's own privacy policy (https://stripe.com/privacy). We do not have access to them.

3. NO ANALYTICS OR TRACKING

The forecastly.me application does not currently run Google Analytics, Meta Pixel, Hotjar, Mixpanel or any equivalent tool. If that changes, we will update this policy AND show you a fresh consent banner with the ability to accept or reject non-essential cookies before any tracking is set.

4. HOW TO CONTROL COOKIES

You can clear cookies and localStorage from your browser settings at any time. Be aware that clearing the session token will log you out. Most browsers also let you block all cookies — if you do this, the forecastly.me application will not work because you cannot stay logged in.

5. QUESTIONS

Email hello@profitmind.co.uk. We are happy to walk you through what is set and why.
`.trim();

// ============================================================
// TERMS OF USE
// ============================================================
export const TERMS_OF_USE = `
1. INTRODUCTION

These terms govern your use of forecastly.me, a cashflow forecasting software service ("the Service") operated by ProfitMIND Ltd (registered in England & Wales). By creating an account or paying for access, you agree to these terms. If you do not agree, please do not use the Service.

2. WHO WE ARE

Legal entity: ProfitMIND Ltd
Trading as: forecastly.me
Postal address: 4 Cranes Farm Road, Basildon, Essex SS14 3JJ, United Kingdom
Email: hello@profitmind.co.uk

3. ELIGIBILITY

You must be at least 18 years old and have authority to enter into this agreement on behalf of yourself or your business.

4. THE SERVICE

forecastly.me helps small business owners and solopreneurs build a 12-month cashflow forecast, log monthly actuals against that forecast, and receive AI-generated narrative insights. The Service is provided "as is" — it is a planning aid, not a substitute for professional accounting, tax or legal advice. The forecast you build is based on the numbers YOU enter; the accuracy of your forecast depends on the accuracy of your inputs.

5. ACCOUNT & SECURITY

You are responsible for keeping your password secure. Do not share your account with others. If you believe your account has been compromised, email hello@profitmind.co.uk immediately and we will help you secure it.

6. PRICING & PAYMENT

The Service is offered in two pricing tiers:
  • One-off: a single payment for lifetime access to the core forecasting feature.
  • Subscription: a setup fee plus a recurring monthly or annual fee for ongoing access to actuals tracking, AI insights, and product updates.

Current prices are shown on the pricing page at the time of purchase. Payments are processed by Stripe. By starting a subscription you authorise Stripe to charge your saved payment method on each renewal date.

You can cancel your subscription at any time from inside the app or via the Stripe customer portal. Cancellation takes effect at the end of your current billing period — we do not pro-rate or refund partial months unless required by consumer law.

7. ACCEPTABLE USE

You agree not to:
  • Resell, sublicense or share access to the Service with anyone outside your business.
  • Attempt to gain unauthorised access to other users' data or to our infrastructure.
  • Use the Service to send spam, malware or anything illegal.
  • Use automated tools to scrape, copy or reverse-engineer the Service beyond what is permitted under UK law.

We may suspend or terminate your account, without refund, if you breach these terms.

8. AI-GENERATED INSIGHTS

The "Forecast Story", goal action plans, variance insights and Big Decision framing are generated by a large language model. We curate the prompts and inject only your numbers + your goals, but the model output is non-deterministic and may contain mistakes. Always sense-check AI output before acting on it. We accept no liability for business decisions taken solely on the basis of AI-generated text.

9. INTELLECTUAL PROPERTY

The Service, its design, copy and underlying code remain the property of ProfitMIND Ltd. You retain ownership of the business data and content you enter — your forecast, your actuals, your goals are yours. You grant us a limited licence to process this data only to run the Service for you.

10. LIMITATION OF LIABILITY

To the maximum extent permitted by law, ProfitMIND Ltd will not be liable for any indirect, consequential or special losses arising from your use of the Service, including but not limited to loss of profit, loss of revenue or loss of business opportunity. Our total liability to you, for any direct loss, in any twelve-month period, is limited to the amount you paid us for the Service in that period. Nothing in these terms excludes our liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot lawfully be excluded.

11. SERVICE AVAILABILITY

We aim for high availability but do not guarantee uninterrupted service. We may take the Service offline for maintenance, security or product changes with reasonable notice where practical. We are not liable for downtime caused by third-party providers (Stripe, MailerLite, Resend, MongoDB Atlas, Anthropic, Emergent).

12. ACCOUNT DELETION

You can delete your account at any time from your account settings. This is irreversible: we will permanently wipe your forecast, actuals, goals and account profile, cancel any active subscription, and remove your email from our MailerLite list. Tax records (payments only) are retained for 6 years as required by HMRC.

13. CHANGES TO THESE TERMS

We may update these terms occasionally. Material changes will be emailed to active customers at least 14 days in advance. The current version is always available at the URL where you first read these.

14. GOVERNING LAW

These terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.

15. CONTACT

For any question about these terms — including refunds, complaints or anything that didn't sit right — email hello@profitmind.co.uk. We aim to reply within 2 working days.
`.trim();
